/**
 * Community management service for Firebase
 * Handles forums, posts, messaging, and community features
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  increment,
  arrayUnion,
  arrayRemove,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db, auth } from './config';
import {
  Forum,
  Post,
  Reply,
  Conversation,
  Message,
  Notification,
  ForumCategory,
  PostType,
} from '../../types/community';

/**
 * Community service class
 */
export class CommunityService {
  private static instance: CommunityService;
  
  public static getInstance(): CommunityService {
    if (!CommunityService.instance) {
      CommunityService.instance = new CommunityService();
    }
    return CommunityService.instance;
  }

  // ==================== FORUMS ====================

  /**
   * Get all forums with optional filtering
   */
  async getForums(filters?: {
    category?: ForumCategory;
    isPrivate?: boolean;
    limit?: number;
  }): Promise<Forum[]> {
    try {
      let q = query(
        collection(db, 'forums'),
        orderBy('lastActivity', 'desc')
      );

      if (filters?.category) {
        q = query(q, where('category', '==', filters.category));
      }

      if (filters?.isPrivate !== undefined) {
        q = query(q, where('isPrivate', '==', filters.isPrivate));
      }

      if (filters?.limit) {
        q = query(q, limit(filters.limit));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastActivity: doc.data().lastActivity?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Forum[];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get a single forum by ID
   */
  async getForum(forumId: string): Promise<Forum | null> {
    try {
      const forumDoc = await getDoc(doc(db, 'forums', forumId));
      
      if (!forumDoc.exists()) {
        return null;
      }

      const data = forumDoc.data();
      return {
        id: forumDoc.id,
        ...data,
        lastActivity: data.lastActivity?.toDate(),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as Forum;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a new forum
   */
  async createForum(forumData: Omit<Forum, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to create forum');
    }

    try {
      const forum = {
        ...forumData,
        createdBy: user.uid,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
        lastActivity: Timestamp.fromDate(new Date()),
      };

      const docRef = await addDoc(collection(db, 'forums'), forum);
      return docRef.id;
    } catch (error) {
      throw error;
    }
  }

  // ==================== POSTS ====================

  /**
   * Get posts from a forum
   */
  async getForumPosts(
    forumId: string,
    filters?: {
      type?: PostType;
      isPinned?: boolean;
      limit?: number;
      lastDoc?: any;
    }
  ): Promise<{ posts: Post[]; hasMore: boolean; lastDoc: any }> {
    try {
      let q = query(
        collection(db, 'posts'),
        where('forumId', '==', forumId),
        where('isDeleted', '==', false),
        orderBy('isPinned', 'desc'),
        orderBy('lastReplyAt', 'desc')
      );

      if (filters?.type) {
        q = query(q, where('type', '==', filters.type));
      }

      const pageLimit = filters?.limit || 20;
      q = query(q, limit(pageLimit + 1));

      if (filters?.lastDoc) {
        q = query(q, startAfter(filters.lastDoc));
      }

      const snapshot = await getDocs(q);
      const posts: Post[] = [];

      snapshot.docs.forEach((doc, index) => {
        if (index < pageLimit) {
          const data = doc.data();
          posts.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
            lastReplyAt: data.lastReplyAt?.toDate(),
            moderatedAt: data.moderatedAt?.toDate(),
          } as Post);
        }
      });

      return {
        posts,
        hasMore: snapshot.docs.length > pageLimit,
        lastDoc: snapshot.docs[pageLimit - 1] || null,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get a single post by ID
   */
  async getPost(postId: string): Promise<Post | null> {
    try {
      const postDoc = await getDoc(doc(db, 'posts', postId));
      
      if (!postDoc.exists()) {
        return null;
      }

      const data = postDoc.data();
      return {
        id: postDoc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        lastReplyAt: data.lastReplyAt?.toDate(),
        moderatedAt: data.moderatedAt?.toDate(),
      } as Post;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a new post
   */
  async createPost(postData: Omit<Post, 'id' | 'createdAt' | 'updatedAt' | 'authorId'>): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to create post');
    }

    try {
      const post = {
        ...postData,
        authorId: user.uid,
        likes: [],
        views: 0,
        replyCount: 0,
        isApproved: true, // Auto-approve for now
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      const docRef = await addDoc(collection(db, 'posts'), post);

      // Update forum post count and last activity
      await updateDoc(doc(db, 'forums', postData.forumId), {
        postCount: increment(1),
        lastActivity: Timestamp.fromDate(new Date()),
      });

      return docRef.id;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Like/unlike a post
   */
  async togglePostLike(postId: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to like post');
    }

    try {
      const postRef = doc(db, 'posts', postId);
      const postDoc = await getDoc(postRef);
      
      if (!postDoc.exists()) {
        throw new Error('Post not found');
      }

      const post = postDoc.data() as Post;
      const hasLiked = post.likes.includes(user.uid);

      if (hasLiked) {
        await updateDoc(postRef, {
          likes: arrayRemove(user.uid),
        });
      } else {
        await updateDoc(postRef, {
          likes: arrayUnion(user.uid),
        });
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Increment post view count
   */
  async incrementPostViews(postId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'posts', postId), {
        views: increment(1),
      });
    } catch (error) {
      throw error;
    }
  }

  // ==================== REPLIES ====================

  /**
   * Get replies for a post
   */
  async getPostReplies(postId: string, maxResults: number = 50): Promise<Reply[]> {
    try {
      const q = query(
        collection(db, 'replies'),
        where('postId', '==', postId),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'asc'),
        limit(maxResults)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        editedAt: doc.data().editedAt?.toDate(),
        moderatedAt: doc.data().moderatedAt?.toDate(),
      })) as Reply[];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a new reply
   */
  async createReply(replyData: Omit<Reply, 'id' | 'createdAt' | 'updatedAt' | 'authorId'>): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to create reply');
    }

    try {
      const reply = {
        ...replyData,
        authorId: user.uid,
        likes: [],
        isApproved: true, // Auto-approve for now
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      const docRef = await addDoc(collection(db, 'replies'), reply);

      // Update post reply count and last reply time
      await updateDoc(doc(db, 'posts', replyData.postId), {
        replyCount: increment(1),
        lastReplyAt: Timestamp.fromDate(new Date()),
      });

      return docRef.id;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Like/unlike a reply
   */
  async toggleReplyLike(replyId: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to like reply');
    }

    try {
      const replyRef = doc(db, 'replies', replyId);
      const replyDoc = await getDoc(replyRef);
      
      if (!replyDoc.exists()) {
        throw new Error('Reply not found');
      }

      const reply = replyDoc.data() as Reply;
      const hasLiked = reply.likes.includes(user.uid);

      if (hasLiked) {
        await updateDoc(replyRef, {
          likes: arrayRemove(user.uid),
        });
      } else {
        await updateDoc(replyRef, {
          likes: arrayUnion(user.uid),
        });
      }
    } catch (error) {
      throw error;
    }
  }

  // ==================== MESSAGING ====================

  /**
   * Get user's conversations
   */
  async getUserConversations(userId: string): Promise<Conversation[]> {
    try {
      
      const q = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', userId),
        where('isActive', '==', true),
        orderBy('lastMessageAt', 'desc')
      );

      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastMessageAt: doc.data().lastMessageAt?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Conversation[];
    } catch (error) {
      console.error('❌ Error loading conversations:', error);
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          code: (error as any).code,
          stack: error.stack
        });
      }
      throw error;
    }
  }

  /**
   * Create or get existing conversation
   */
  async createOrGetConversation(participantIds: string[]): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to create conversation');
    }

    try {
      // For direct messages, check if conversation already exists
      if (participantIds.length === 2) {
        const q = query(
          collection(db, 'conversations'),
          where('participants', '==', participantIds.sort()),
          where('type', '==', 'direct'),
          limit(1)
        );

        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          return snapshot.docs[0].id;
        }
      }

      // Create new conversation
      const conversation = {
        participants: participantIds.sort(),
        type: participantIds.length === 2 ? 'direct' : 'group',
        isActive: true,
        isArchived: false,
        createdBy: user.uid,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      const docRef = await addDoc(collection(db, 'conversations'), conversation);
      return docRef.id;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Send a message
   */
  async sendMessage(messageData: Omit<Message, 'id' | 'createdAt' | 'updatedAt' | 'senderId'>): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
      console.error('❌ User not authenticated for sending message');
      throw new Error('User must be authenticated to send message');
    }

    try {
      const message = {
        ...messageData,
        senderId: user.uid,
        readBy: [{ userId: user.uid, readAt: Timestamp.fromDate(new Date()) }],
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      const docRef = await addDoc(collection(db, 'messages'), message);

      // Update conversation with last message info
      await updateDoc(doc(db, 'conversations', messageData.conversationId), {
        lastMessageId: docRef.id,
        lastMessageAt: Timestamp.fromDate(new Date()),
        lastMessagePreview: messageData.content.substring(0, 100),
        updatedAt: Timestamp.fromDate(new Date()),
      });

      return docRef.id;
    } catch (error) {
      console.error('❌ Error sending message:', error);
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          code: (error as any).code,
          stack: error.stack
        });
      }
      throw error;
    }
  }

  /**
   * Get messages from a conversation
   */
  async getConversationMessages(
    conversationId: string,
    maxResults: number = 50,
    lastDoc?: any
  ): Promise<{ messages: Message[]; hasMore: boolean; lastDoc: any }> {
    try {
      let q = query(
        collection(db, 'messages'),
        where('conversationId', '==', conversationId),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'desc'),
        limit(maxResults + 1)
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const messages: Message[] = [];

      snapshot.docs.forEach((doc, index) => {
        if (index < maxResults) {
          const data = doc.data();
          messages.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
            editedAt: data.editedAt?.toDate(),
            deletedAt: data.deletedAt?.toDate(),
            readBy: data.readBy?.map((read: any) => ({
              ...read,
              readAt: read.readAt?.toDate(),
            })) || [],
          } as Message);
        }
      });

      return {
        messages: messages.reverse(), // Reverse to show oldest first
        hasMore: snapshot.docs.length > maxResults,
        lastDoc: snapshot.docs[maxResults - 1] || null,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Mark message as read
   */
  async markMessageAsRead(messageId: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const messageRef = doc(db, 'messages', messageId);
      const messageDoc = await getDoc(messageRef);
      
      if (!messageDoc.exists()) return;

      const message = messageDoc.data() as Message;
      const hasRead = message.readBy?.some(read => read.userId === user.uid);

      if (!hasRead) {
        await updateDoc(messageRef, {
          readBy: arrayUnion({
            userId: user.uid,
            readAt: Timestamp.fromDate(new Date()),
          }),
        });
      }
    } catch (error) {
      throw error;
    }
  }

  // ==================== REAL-TIME SUBSCRIPTIONS ====================

  /**
   * Subscribe to conversation messages
   */
  subscribeToConversationMessages(
    conversationId: string,
    callback: (messages: Message[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, 'messages'),
      where('conversationId', '==', conversationId),
      where('isDeleted', '==', false),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        editedAt: doc.data().editedAt?.toDate(),
        deletedAt: doc.data().deletedAt?.toDate(),
        readBy: doc.data().readBy?.map((read: any) => ({
          ...read,
          readAt: read.readAt?.toDate(),
        })) || [],
      })) as Message[];

      callback(messages.reverse());
    });
  }

  /**
   * Subscribe to user's conversations
   */
  subscribeToUserConversations(
    userId: string,
    callback: (conversations: Conversation[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userId),
      where('isActive', '==', true),
      orderBy('lastMessageAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const conversations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastMessageAt: doc.data().lastMessageAt?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Conversation[];

      callback(conversations);
    });
  }

  // ==================== NOTIFICATIONS ====================

  /**
   * Get user notifications
   */
  async getUserNotifications(userId: string, maxResults: number = 20): Promise<Notification[]> {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(maxResults)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        readAt: doc.data().readAt?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Notification[];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Mark notification as read
   */
  async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        isRead: true,
        readAt: Timestamp.fromDate(new Date()),
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create notification
   */
  async createNotification(notificationData: Omit<Notification, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const notification = {
        ...notificationData,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      const docRef = await addDoc(collection(db, 'notifications'), notification);
      return docRef.id;
    } catch (error) {
      throw error;
    }
  }
}

// Export singleton instance
export const communityService = CommunityService.getInstance();

export default communityService;

